import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { BaseApiService } from './common/base-api.service';
import { API } from '../constants/api';
import { LocalStorageService } from './common/localstorage-service';

export interface NotificationDataPayload {
  eventKey?: string;
  event_key?: string;
  entityType?: string;
  entity_type?: string;
  entityId?: string | number;
  entity_id?: string | number;
  route?: string;
  url?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private baseApiService = inject(BaseApiService);
  private localStorageService = inject(LocalStorageService);
  private router = inject(Router);

  unreadCount = signal<number>(0);

  getMyNotifications(
    params?: HttpParams | { [param: string]: any },
  ): Observable<any> {
    return this.baseApiService.get(API.notification.my, params);
  }

  markAsRead(recipientMappingId: number | string): Observable<any> {
    return this.baseApiService.put(
      API.notification.markAsRead(recipientMappingId),
      {},
    );
  }

  markAllAsRead(): Observable<any> {
    return this.baseApiService.put(API.notification.markAllAsRead, {});
  }

  updatePreferences(payload: any[]): Observable<any> {
    return this.baseApiService.put(API.notification.preferences, payload);
  }

  getUnreadCount(): Observable<any> {
    const user = this.localStorageService.get('user');
    const token = this.localStorageService.get('token');
    if (!user || !token) {
      return of({ data: { unread_count: 0 } });
    }

    return this.baseApiService.get(API.notification.unreadCount).pipe(
      tap((res: any) => {
        const countVal =
          typeof res === 'number' ? res : (res?.data?.unread_count ?? 0);

        if (typeof countVal === 'number') {
          this.unreadCount.set(countVal);
        }
      }),
    );
  }

  /**
   * 🧭 Returns the target route for any notification data payload based on event_key and entity_type
   */
  getRouteByNotification(data: NotificationDataPayload | any): any[] | string {
    if (!data) return ['/home'];

    if (data.route) return data.route;
    if (data.url) return data.url;

    const eventKey = (data.eventKey || data.event_key || '').toLowerCase();
    const entityType = (data.entityType || data.entity_type || '').toLowerCase();
    const entityId = data.entityId || data.entity_id;

    switch (eventKey) {
      // 📋 Lead Management Events
      case 'lead_created':
      case 'document_upload_link_sent':
      case 'documents_received':
      case 'documents_updated':
      case 'price_offer_shared':
      case 'self_delivery_confirmed':
      case 'payment_released':
      case 'meeting_scheduled':
      case 'meeting_reminder':
      case 'lead_assigned':
      case 'lead_transferred_to_new_owner':
        return entityId
          ? ['/home/lead-management/lead/edit', entityId]
          : ['/home/lead-management/lead'];

      case 'lead_transferred_to_previous_owner':
        return ['/home/lead-management/lead'];

      // 🚚 Pickup Scheduled Events
      case 'pickup_scheduled':
      case 'pickup_assigned':
      case 'vehicle_picked_up':
        return entityId
          ? ['/home/lead-management/pickup-scheduled/edit', entityId]
          : ['/home/lead-management/pickup-scheduled'];

      // 🚗 Parking / Inward Events
      case 'vehicle_in_transit':
        return ['/home/parking/vehicle-inward'];

      case 'vehicle_received_accepted':
      case 'vehicle_delivered':
        return entityId
          ? ['/home/parking/vehicle-inward/edit', entityId]
          : ['/home/parking/vehicle-inward'];

      // 📊 Dashboard / Summary Events
      case 'agent_daily_summary':
      case 'agent_weekly_summary':
        return ['/home/dashboard/business-dashboard'];

      case 'daily_worklist_digest':
        return ['/home/dashboard/procurement-leads'];

      // 🔄 Fallback by entityType
      default: {
        switch (entityType) {
          case 'vehicle_lead':
          case 'lead':
            return entityId
              ? ['/home/lead-management/lead/edit', entityId]
              : ['/home/lead-management/lead'];

          case 'pickup':
            return entityId
              ? ['/home/lead-management/pickup-scheduled/edit', entityId]
              : ['/home/lead-management/pickup-scheduled'];

          case 'vehicle_inward':
          case 'inward':
            return entityId
              ? ['/home/parking/vehicle-inward/edit', entityId]
              : ['/home/parking/vehicle-inward'];

          case 'job_card':
            return entityId
              ? ['/home/workshop/job-card/edit', entityId]
              : ['/home/workshop/job-card'];

          case 'dismantling':
            return entityId
              ? ['/home/workshop/dismantling/edit', entityId]
              : ['/home/workshop/dismantling'];

          case 'cod_csv':
            return entityId
              ? ['/home/compliance/cod-csv/edit', entityId]
              : ['/home/compliance/cod-csv'];

          default:
            return ['/home'];
        }
      }
    }
  }

  /**
   * 🚀 Navigates the application to the corresponding screen for a notification
   */
  navigateToNotification(data: NotificationDataPayload | any): void {
    const target = this.getRouteByNotification(data);
    if (typeof target === 'string') {
      this.router.navigateByUrl(target);
    } else if (Array.isArray(target)) {
      this.router.navigate(target);
    }
  }
}
